import React, { useState, useEffect, useRef } from 'react';
import { 
  ShoppingCart, 
  Package, 
  History, 
  Send, 
  Camera, 
  Trash2, 
  Plus, 
  Minus, 
  Search,
  User,
  Smartphone,
  AlertTriangle,
  CheckCircle,
  BarChart3,
  Settings,
  Store,
  MapPin,
  Phone,
  Edit,
  Save,
  X,
  Shield,
  Users,
  Download,
  Upload,
  LogOut,
  LogIn,
  Eye,
  EyeOff,
  Lock,
  Barcode,
  QrCode,
  Scan,
  Truck,
  PackageOpen,
  Calculator
} from 'lucide-react';
import './App.css';

const WhatsAppPOSSystem = () => {
  // Enhanced user management with authentication
  const [users, setUsers] = useState([
    {
      id: 1,
      username: 'admin',
      password: 'admin123',
      name: 'Kwame Mensah',
      role: 'admin',
      shopId: 1,
      phone: '+233501234567',
      isActive: true,
      permissions: ['all']
    },
    {
      id: 2,
      username: 'manager',
      password: 'manager123',
      name: 'Ama Serwaa',
      role: 'manager',
      shopId: 1,
      phone: '+233502345678',
      isActive: true,
      permissions: ['sales', 'inventory', 'reports']
    },
    {
      id: 3,
      username: 'cashier',
      password: 'cashier123',
      name: 'Kofi Boateng',
      role: 'employee',
      shopId: 1,
      phone: '+233503456789',
      isActive: true,
      permissions: ['sales']
    }
  ]);

  const [currentUser, setCurrentUser] = useState(null);
  const [showLogin, setShowLogin] = useState(true);
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);

  // Multi-shop support
  const [shops, setShops] = useState([
    {
      id: 1,
      name: 'Accra Central Shop',
      location: 'Accra Central, Ghana',
      phone: '+233501234567',
      ownerName: 'Kwame Mensah',
      currency: 'GH₵',
      taxRate: 3,
      whatsappNumbers: [
        { id: 1, number: '+233501234567', name: 'Owner', isActive: true },
        { id: 2, number: '+233502345678', name: 'Manager', isActive: true }
      ]
    },
    {
      id: 2,
      name: 'Kumasi Branch',
      location: 'Kumasi, Ghana',
      phone: '+233502234567',
      ownerName: 'Ama Serwaa',
      currency: 'GH₵',
      taxRate: 3,
      whatsappNumbers: [
        { id: 1, number: '+233502234567', name: 'Owner', isActive: true }
      ]
    }
  ]);

  const [currentShopId, setCurrentShopId] = useState(1);
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [sales, setSales] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('pos');
  const [customerPhone, setCustomerPhone] = useState('');
  const [notifications, setNotifications] = useState([]);
  const [editingShop, setEditingShop] = useState(null);
  const [editingWhatsapp, setEditingWhatsapp] = useState(null);
  const [newWhatsappNumber, setNewWhatsappNumber] = useState({ number: '', name: '' });

  // New Inventory Management States
  const [inventoryReceiving, setInventoryReceiving] = useState({
    show: false,
    productCode: '',
    productName: '',
    quantity: '',
    costPrice: '',
    sellingPrice: '',
    category: '',
    barcode: ''
  });

  const [barcodeScanner, setBarcodeScanner] = useState({
    show: false,
    mode: 'inventory', // 'inventory' or 'checkout'
    scannedData: ''
  });

  const [inventoryHistory, setInventoryHistory] = useState([]);
  const videoRef = useRef(null);
  const [scanning, setScanning] = useState(false);

  // Get current shop
  const currentShop = shops.find(shop => shop.id === currentShopId) || shops[0];

  // Check user permissions
  const hasPermission = (permission) => {
    if (!currentUser) return false;
    if (currentUser.role === 'admin') return true;
    return currentUser.permissions.includes('all') || currentUser.permissions.includes(permission);
  };

  // Initialize products for current shop
  useEffect(() => {
    const shopProducts = [
      { 
        id: 1, 
        name: 'Coca Cola 500ml', 
        price: 3.50, 
        costPrice: 2.00, 
        stock: 45, 
        sku: 'COKE-500', 
        barcode: '123456789012',
        category: 'Beverages', 
        shopId: currentShopId,
        totalReceived: 200,
        totalSold: 155
      },
      { 
        id: 2, 
        name: 'Voltic Water 750ml', 
        price: 2.00, 
        costPrice: 1.00, 
        stock: 30, 
        sku: 'VOLT-750', 
        barcode: '123456789013',
        category: 'Beverages', 
        shopId: currentShopId,
        totalReceived: 100,
        totalSold: 70
      },
      { 
        id: 3, 
        name: 'Indomie Chicken', 
        price: 1.50, 
        costPrice: 0.80, 
        stock: 120, 
        sku: 'INDO-CHK', 
        barcode: '123456789014',
        category: 'Food', 
        shopId: currentShopId,
        totalReceived: 300,
        totalSold: 180
      },
    ].filter(product => product.shopId === currentShopId);
    
    setProducts(shopProducts);
    setCart([]);
  }, [currentShopId]);

  // Auto-login for demo
  useEffect(() => {
    const demoLogin = () => {
      setCurrentUser(users[0]); // Auto-login as admin
      setShowLogin(false);
    };
    demoLogin();
  }, []);

  // Login function
  const handleLogin = (e) => {
    e.preventDefault();
    const user = users.find(u => 
      u.username === loginForm.username && 
      u.password === loginForm.password &&
      u.isActive
    );

    if (user) {
      setCurrentUser(user);
      setShowLogin(false);
      setLoginForm({ username: '', password: '' });
      addNotification(`Welcome back, ${user.name}!`, 'success');
    } else {
      addNotification('Invalid username or password', 'error');
    }
  };

  // Logout function
  const handleLogout = () => {
    setCurrentUser(null);
    setShowLogin(true);
    setCart([]);
    setActiveTab('pos');
    addNotification('Logged out successfully', 'info');
  };

  // Switch user (for demo purposes - admin can switch to other roles)
  const switchUser = (userId) => {
    const user = users.find(u => u.id === userId);
    if (user) {
      setCurrentUser(user);
      addNotification(`Switched to ${user.name} (${user.role})`, 'info');
    }
  };

  // Generate unique receipt code with shop prefix
  const generateReceiptCode = () => {
    const date = new Date();
    const shopPrefix = currentShop.name.substring(0, 3).toUpperCase();
    return `${shopPrefix}${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}-${String(sales.filter(s => s.shopId === currentShopId).length + 1).padStart(4, '0')}`;
  };

  // Inventory Management Functions
  const receiveInventory = () => {
    if (!inventoryReceiving.productCode || !inventoryReceiving.quantity) {
      addNotification('Please enter product code and quantity', 'error');
      return;
    }

    const quantity = parseInt(inventoryReceiving.quantity);
    const costPrice = parseFloat(inventoryReceiving.costPrice) || 0;
    const sellingPrice = parseFloat(inventoryReceiving.sellingPrice) || costPrice * 1.5;

    // Check if product exists
    const existingProduct = products.find(p => 
      p.sku === inventoryReceiving.productCode || 
      p.barcode === inventoryReceiving.productCode ||
      p.name.toLowerCase() === inventoryReceiving.productCode.toLowerCase()
    );

    if (existingProduct) {
      // Update existing product
      const updatedProducts = products.map(p => 
        p.id === existingProduct.id 
          ? { 
              ...p, 
              stock: p.stock + quantity,
              costPrice: costPrice || p.costPrice,
              price: sellingPrice || p.price,
              totalReceived: p.totalReceived + quantity
            }
          : p
      );
      setProducts(updatedProducts);
      addNotification(`Updated ${existingProduct.name} - Added ${quantity} units`, 'success');
    } else {
      // Create new product
      const newProduct = {
        id: Date.now(),
        name: inventoryReceiving.productName || `Product ${inventoryReceiving.productCode}`,
        price: sellingPrice,
        costPrice: costPrice,
        stock: quantity,
        sku: inventoryReceiving.productCode,
        barcode: inventoryReceiving.barcode || inventoryReceiving.productCode,
        category: inventoryReceiving.category || 'General',
        shopId: currentShopId,
        totalReceived: quantity,
        totalSold: 0
      };
      setProducts([...products, newProduct]);
      addNotification(`Added new product: ${newProduct.name}`, 'success');
    }

    // Add to inventory history
    const inventoryRecord = {
      id: Date.now(),
      productCode: inventoryReceiving.productCode,
      productName: existingProduct?.name || inventoryReceiving.productName,
      quantity: quantity,
      costPrice: costPrice,
      timestamp: new Date().toISOString(),
      receivedBy: currentUser.name,
      type: existingProduct ? 'restock' : 'new'
    };
    setInventoryHistory([inventoryRecord, ...inventoryHistory]);

    // Send WhatsApp notification to owners
    sendInventoryNotification(inventoryRecord, existingProduct);

    // Reset form
    setInventoryReceiving({
      show: false,
      productCode: '',
      productName: '',
      quantity: '',
      costPrice: '',
      sellingPrice: '',
      category: '',
      barcode: ''
    });
  };

  // Send inventory notification to WhatsApp
  const sendInventoryNotification = (inventoryRecord, existingProduct) => {
    const activeNumbers = currentShop.whatsappNumbers.filter(w => w.isActive);
    
    const message = `
📦 *INVENTORY UPDATE - ${currentShop.name}*
━━━━━━━━━━━━━━━━━━
🆕 *${inventoryRecord.type === 'new' ? 'NEW PRODUCT' : 'RESTOCK'}*
📛 Product: ${inventoryRecord.productName}
🔢 Code: ${inventoryRecord.productCode}
📦 Quantity: +${inventoryRecord.quantity} units
💰 Cost: ${currentShop.currency}${inventoryRecord.costPrice.toFixed(2)}
👤 Received by: ${inventoryRecord.receivedBy}
⏰ ${new Date(inventoryRecord.timestamp).toLocaleString()}
${existingProduct ? `📊 Total Stock: ${existingProduct.stock + inventoryRecord.quantity} units` : ''}
    `.trim();

    // Send to all active WhatsApp numbers
    activeNumbers.forEach(whatsapp => {
      console.log(`📱 Inventory update to ${whatsapp.name} (${whatsapp.number}):`, message);
    });

    addNotification(`📱 Inventory update sent to ${activeNumbers.length} WhatsApp numbers`, 'info');
  };

  // Barcode Scanner Simulation
  const simulateBarcodeScan = (mode = 'checkout') => {
    setBarcodeScanner({
      show: true,
      mode: mode,
      scannedData: ''
    });
    setScanning(true);

    // Simulate scanning process
    setTimeout(() => {
      const sampleBarcodes = ['123456789012', '123456789013', '123456789014'];
      const randomBarcode = sampleBarcodes[Math.floor(Math.random() * sampleBarcodes.length)];
      
      setBarcodeScanner(prev => ({
        ...prev,
        scannedData: randomBarcode
      }));
      setScanning(false);

      // Auto-process after scan
      setTimeout(() => {
        processScannedBarcode(randomBarcode, mode);
      }, 1000);
    }, 2000);
  };

  const processScannedBarcode = (barcode, mode) => {
    const product = products.find(p => p.barcode === barcode || p.sku === barcode);
    
    if (product) {
      if (mode === 'checkout') {
        // Add to cart for checkout
        addToCart(product);
        addNotification(`Scanned: ${product.name}`, 'success');
      } else {
        // Fill inventory form
        setInventoryReceiving(prev => ({
          ...prev,
          productCode: product.sku,
          productName: product.name,
          costPrice: product.costPrice.toString(),
          sellingPrice: product.price.toString(),
          category: product.category,
          barcode: product.barcode
        }));
        addNotification(`Product found: ${product.name}`, 'success');
      }
    } else {
      addNotification('Product not found! Please add product first.', 'error');
      if (mode === 'inventory') {
        setInventoryReceiving(prev => ({
          ...prev,
          productCode: barcode,
          barcode: barcode
        }));
      }
    }

    setBarcodeScanner({
      show: false,
      mode: 'checkout',
      scannedData: ''
    });
  };

  // Quick inventory by product code
  const quickInventoryAdd = (productCode, quantity = 1) => {
    const product = products.find(p => 
      p.sku === productCode || 
      p.barcode === productCode ||
      p.name.toLowerCase().includes(productCode.toLowerCase())
    );

    if (product) {
      const updatedProducts = products.map(p => 
        p.id === product.id 
          ? { 
              ...p, 
              stock: p.stock + quantity,
              totalReceived: p.totalReceived + quantity
            }
          : p
      );
      setProducts(updatedProducts);

      const inventoryRecord = {
        id: Date.now(),
        productCode: product.sku,
        productName: product.name,
        quantity: quantity,
        costPrice: product.costPrice,
        timestamp: new Date().toISOString(),
        receivedBy: currentUser.name,
        type: 'quick_restock'
      };
      setInventoryHistory([inventoryRecord, ...inventoryHistory]);

      addNotification(`Quick restock: ${product.name} (+${quantity})`, 'success');
      sendInventoryNotification(inventoryRecord, product);
    } else {
      addNotification('Product not found! Use full inventory form.', 'error');
    }
  };

  // Add product to cart
  const addToCart = (product) => {
    if (product.stock === 0) {
      addNotification(`${product.name} is out of stock!`, 'error');
      return;
    }

    const existing = cart.find(item => item.id === product.id);
    if (existing) {
      if (existing.quantity >= product.stock) {
        addNotification(`Only ${product.stock} ${product.name} available!`, 'warning');
        return;
      }
      setCart(cart.map(item => 
        item.id === product.id 
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, { ...product, quantity: 1 }]);
    }
    addNotification(`Added ${product.name} to cart`, 'success');
  };

  // Update cart quantity
  const updateQuantity = (id, change) => {
    setCart(cart.map(item => {
      if (item.id === id) {
        const newQty = item.quantity + change;
        const product = products.find(p => p.id === id);
        
        if (newQty > product.stock) {
          addNotification(`Only ${product.stock} ${product.name} available!`, 'warning');
          return item;
        }
        
        return newQty > 0 ? { ...item, quantity: newQty } : item;
      }
      return item;
    }).filter(item => item.quantity > 0));
  };

  // Remove from cart
  const removeFromCart = (id) => {
    const item = cart.find(item => item.id === id);
    setCart(cart.filter(item => item.id !== id));
    addNotification(`Removed ${item.name} from cart`, 'info');
  };

  // Calculate total
  const calculateTotal = () => {
    return cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  // Calculate tax
  const calculateTax = () => {
    return (calculateTotal() * currentShop.taxRate) / 100;
  };

  // Calculate final total
  const calculateFinalTotal = () => {
    return calculateTotal() + calculateTax();
  };

  // Calculate profit
  const calculateProfit = () => {
    return cart.reduce((sum, item) => sum + ((item.price - item.costPrice) * item.quantity), 0);
  };

  // Process sale
  const processSale = () => {
    if (cart.length === 0) {
      addNotification('Cart is empty! Add products first.', 'error');
      return;
    }

    const receiptCode = generateReceiptCode();
    const total = calculateTotal();
    const tax = calculateTax();
    const finalTotal = calculateFinalTotal();
    const profit = calculateProfit();

    // Create sale record
    const sale = {
      id: Date.now(),
      code: receiptCode,
      items: [...cart],
      total: total,
      tax: tax,
      finalTotal: finalTotal,
      profit: profit,
      customerPhone: customerPhone,
      timestamp: new Date().toISOString(),
      type: 'digital',
      shopId: currentShopId,
      shopName: currentShop.name,
      soldBy: currentUser.name,
      userId: currentUser.id
    };

    // Update inventory and track sales
    const updatedProducts = products.map(product => {
      const cartItem = cart.find(item => item.id === product.id);
      if (cartItem) {
        return { 
          ...product, 
          stock: product.stock - cartItem.quantity,
          totalSold: product.totalSold + cartItem.quantity
        };
      }
      return product;
    });

    setProducts(updatedProducts);
    setSales([...sales, sale]);
    
    // Send WhatsApp notification
    sendSaleNotification(sale);
    
    // Clear cart and show success
    setCart([]);
    setCustomerPhone('');
    
    addNotification(`Sale completed! Receipt: ${receiptCode}`, 'success');
    
    // Check for low stock items
    checkLowStock(updatedProducts);
  };

  // Send sale notification to WhatsApp
  const sendSaleNotification = (sale) => {
    const activeNumbers = currentShop.whatsappNumbers.filter(w => w.isActive);
    
    const itemsList = sale.items.map(item => 
      `${item.name} x${item.quantity} - ${currentShop.currency}${(item.price * item.quantity).toFixed(2)}`
    ).join('\n');

    const message = `
🛍️ *NEW SALE - ${sale.code}*
🏪 *${currentShop.name}*
👤 *Sold by: ${sale.soldBy}*
━━━━━━━━━━━━━━━━━━
${itemsList}
━━━━━━━━━━━━━━━━━━
💰 *Subtotal: ${currentShop.currency}${sale.total.toFixed(2)}*
📊 *Tax (${currentShop.taxRate}%): ${currentShop.currency}${sale.tax.toFixed(2)}*
💵 *Total: ${currentShop.currency}${sale.finalTotal.toFixed(2)}*
🎯 *Profit: ${currentShop.currency}${sale.profit.toFixed(2)}*
📱 ${sale.customerPhone ? `Customer: ${sale.customerPhone}` : 'Walk-in Customer'}
⏰ ${new Date(sale.timestamp).toLocaleString()}
    `.trim();

    // Send to all active WhatsApp numbers
    activeNumbers.forEach(whatsapp => {
      console.log(`📱 Sale notification to ${whatsapp.name} (${whatsapp.number}):`, message);
    });

    // Send to customer if number provided
    if (sale.customerPhone) {
      console.log(`📱 Sale receipt to customer (${sale.customerPhone}):`, message);
    }

    addNotification(`📱 Receipt sent to ${activeNumbers.length} WhatsApp numbers`, 'info');
  };

  // Check low stock
  const checkLowStock = (productList) => {
    const lowStockItems = productList.filter(p => p.stock < 10);
    lowStockItems.forEach(product => {
      addNotification(`⚠️ Low stock: ${product.name} (${product.stock} left)`, 'warning');
    });
  };

  // Add notification
  const addNotification = (message, type) => {
    const notification = {
      id: Date.now(),
      message,
      type,
      timestamp: new Date()
    };
    
    setNotifications(prev => [notification, ...prev.slice(0, 4)]);
    
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== notification.id));
    }, 5000);
  };

  // Shop management functions (same as before)
  const updateShop = (updatedShop) => {
    if (!hasPermission('settings')) {
      addNotification('You do not have permission to edit shop settings', 'error');
      return;
    }
    
    setShops(shops.map(shop => 
      shop.id === updatedShop.id ? updatedShop : shop
    ));
    setEditingShop(null);
    addNotification('Shop updated successfully!', 'success');
  };

  const addWhatsappNumber = () => {
    if (!hasPermission('settings')) {
      addNotification('You do not have permission to manage WhatsApp numbers', 'error');
      return;
    }

    if (!newWhatsappNumber.number || !newWhatsappNumber.name) {
      addNotification('Please enter both number and name', 'error');
      return;
    }

    const updatedShop = {
      ...currentShop,
      whatsappNumbers: [
        ...currentShop.whatsappNumbers,
        {
          id: Date.now(),
          number: newWhatsappNumber.number,
          name: newWhatsappNumber.name,
          isActive: true
        }
      ]
    };

    updateShop(updatedShop);
    setNewWhatsappNumber({ number: '', name: '' });
  };

  // Get sales analytics for current shop
  const getSalesAnalytics = () => {
    const shopSales = sales.filter(sale => sale.shopId === currentShopId);
    const totalRevenue = shopSales.reduce((sum, sale) => sum + sale.finalTotal, 0);
    const totalProfit = shopSales.reduce((sum, sale) => sum + sale.profit, 0);
    const totalSales = shopSales.length;
    
    return { totalRevenue, totalProfit, totalSales };
  };

  // Filter products by search
  const filteredProducts = products.filter(product => 
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const analytics = getSalesAnalytics();

  // Login Form (same as before)
  if (showLogin || !currentUser) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <div className="logo">🏪</div>
            <h1>Ghana POS System</h1>
            <p>Please login to continue</p>
          </div>

          <form onSubmit={handleLogin} className="login-form">
            <div className="form-group">
              <label>Username</label>
              <input
                type="text"
                value={loginForm.username}
                onChange={(e) => setLoginForm({...loginForm, username: e.target.value})}
                placeholder="Enter your username"
                required
              />
            </div>

            <div className="form-group">
              <label>Password</label>
              <div className="password-input">
                <input
                  type={showPassword ? "text" : "password"}
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                  placeholder="Enter your password"
                  required
                />
                <button 
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="password-toggle"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button type="submit" className="login-btn">
              <LogIn size={20} />
              Sign In
            </button>
          </form>

          <div className="demo-accounts">
            <h3>Demo Accounts:</h3>
            <div className="account-list">
              {users.map(user => (
                <div key={user.id} className="account-item">
                  <strong>{user.name}</strong>
                  <span>Role: {user.role}</span>
                  <span>Username: {user.username}</span>
                  <span>Password: {user.password}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      {/* Notifications */}
      <div className="notifications-container">
        {notifications.map(notification => (
          <div key={notification.id} className={`notification ${notification.type}`}>
            <div className="notification-content">
              {notification.type === 'success' && <CheckCircle size={16} />}
              {notification.type === 'error' && <AlertTriangle size={16} />}
              {notification.type === 'warning' && <AlertTriangle size={16} />}
              {notification.type === 'info' && <Smartphone size={16} />}
              <span>{notification.message}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Barcode Scanner Modal */}
      {barcodeScanner.show && (
        <div className="scanner-modal">
          <div className="scanner-content">
            <div className="scanner-header">
              <h3>
                <Scan size={24} />
                Barcode Scanner - {barcodeScanner.mode === 'checkout' ? 'Checkout' : 'Inventory'}
              </h3>
              <button 
                onClick={() => setBarcodeScanner({ show: false, mode: 'checkout', scannedData: '' })}
                className="close-btn"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="scanner-view">
              {scanning ? (
                <div className="scanning-animation">
                  <div className="scan-line"></div>
                  <p>Scanning barcode...</p>
                </div>
              ) : (
                <div className="scan-result">
                  <Barcode size={48} />
                  <h4>Barcode Scanned!</h4>
                  <p>Code: {barcodeScanner.scannedData}</p>
                  <p>Processing product...</p>
                </div>
              )}
            </div>
            
            <div className="scanner-actions">
              <button 
                onClick={() => simulateBarcodeScan(barcodeScanner.mode)}
                className="btn-primary"
              >
                <Scan size={16} />
                Scan Again
              </button>
              <button 
                onClick={() => setBarcodeScanner({ show: false, mode: 'checkout', scannedData: '' })}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Inventory Receiving Modal */}
      {inventoryReceiving.show && (
        <div className="inventory-modal">
          <div className="inventory-content">
            <div className="inventory-header">
              <h3>
                <Truck size={24} />
                Receive Inventory
              </h3>
              <button 
                onClick={() => setInventoryReceiving({ ...inventoryReceiving, show: false })}
                className="close-btn"
              >
                <X size={20} />
              </button>
            </div>

            <div className="inventory-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Product Code/SKU/Name</label>
                  <div className="input-with-action">
                    <input
                      type="text"
                      value={inventoryReceiving.productCode}
                      onChange={(e) => setInventoryReceiving({...inventoryReceiving, productCode: e.target.value})}
                      placeholder="Enter product code, SKU, or name"
                    />
                    <button 
                      onClick={() => simulateBarcodeScan('inventory')}
                      className="scan-btn"
                    >
                      <Scan size={16} />
                    </button>
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Quantity</label>
                  <input
                    type="number"
                    value={inventoryReceiving.quantity}
                    onChange={(e) => setInventoryReceiving({...inventoryReceiving, quantity: e.target.value})}
                    placeholder="Number of units"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Product Name (Auto-filled)</label>
                  <input
                    type="text"
                    value={inventoryReceiving.productName}
                    onChange={(e) => setInventoryReceiving({...inventoryReceiving, productName: e.target.value})}
                    placeholder="Product name will auto-fill"
                  />
                </div>
                
                <div className="form-group">
                  <label>Category</label>
                  <select
                    value={inventoryReceiving.category}
                    onChange={(e) => setInventoryReceiving({...inventoryReceiving, category: e.target.value})}
                  >
                    <option value="">Select Category</option>
                    <option value="Beverages">Beverages</option>
                    <option value="Food">Food</option>
                    <option value="Bakery">Bakery</option>
                    <option value="Snacks">Snacks</option>
                    <option value="Household">Household</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Cost Price (GH₵)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={inventoryReceiving.costPrice}
                    onChange={(e) => setInventoryReceiving({...inventoryReceiving, costPrice: e.target.value})}
                    placeholder="Cost per unit"
                  />
                </div>
                
                <div className="form-group">
                  <label>Selling Price (GH₵)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={inventoryReceiving.sellingPrice}
                    onChange={(e) => setInventoryReceiving({...inventoryReceiving, sellingPrice: e.target.value})}
                    placeholder="Selling price per unit"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Barcode (Optional)</label>
                <input
                  type="text"
                  value={inventoryReceiving.barcode}
                  onChange={(e) => setInventoryReceiving({...inventoryReceiving, barcode: e.target.value})}
                  placeholder="Barcode number"
                />
              </div>
            </div>

            <div className="inventory-actions">
              <button onClick={receiveInventory} className="btn-primary large">
                <PackageOpen size={20} />
                Receive Inventory & Notify Owner
              </button>
              <button 
                onClick={() => setInventoryReceiving({ ...inventoryReceiving, show: false })}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>

            {/* Quick Inventory Actions */}
            <div className="quick-inventory">
              <h4>Quick Restock</h4>
              <div className="quick-buttons">
                <button onClick={() => quickInventoryAdd('COKE-500', 10)}>
                  +10 Coca Cola
                </button>
                <button onClick={() => quickInventoryAdd('VOLT-750', 10)}>
                  +10 Voltic Water
                </button>
                <button onClick={() => quickInventoryAdd('INDO-CHK', 20)}>
                  +20 Indomie
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="app-header">
        <div className="header-content">
          <div className="header-brand">
            <div className="logo">🏪</div>
            <div>
              <h1>Multi-Shop POS System</h1>
              <p>Ghana Business Management</p>
            </div>
          </div>
          
          <div className="header-controls">
            {/* Shop Selector */}
            {hasPermission('settings') && (
              <div className="shop-selector">
                <Store size={16} />
                <select 
                  value={currentShopId} 
                  onChange={(e) => setCurrentShopId(Number(e.target.value))}
                  className="shop-dropdown"
                >
                  {shops.map(shop => (
                    <option key={shop.id} value={shop.id}>
                      {shop.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            <div className="header-stats">
              <div className="stat">
                <span className="stat-value">{currentShop.currency}{analytics.totalRevenue.toFixed(2)}</span>
                <span className="stat-label">Revenue</span>
              </div>
              <div className="stat">
                <span className="stat-value">{analytics.totalSales}</span>
                <span className="stat-label">Sales</span>
              </div>
              
              {/* User Menu with Role Switching */}
              <div className="user-menu">
                <div className="user-info">
                  <User size={16} />
                  <div className="user-details">
                    <strong>{currentUser.name}</strong>
                    <span className="user-role">{currentUser.role}</span>
                  </div>
                </div>
                
                {/* Role Switch Dropdown (Admin only) */}
                {currentUser.role === 'admin' && (
                  <div className="role-switcher">
                    <select 
                      value={currentUser.id}
                      onChange={(e) => switchUser(Number(e.target.value))}
                      className="role-dropdown"
                    >
                      {users.map(user => (
                        <option key={user.id} value={user.id}>
                          {user.name} ({user.role})
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                
                <button onClick={handleLogout} className="logout-btn">
                  <LogOut size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs - Role-based access */}
      <nav className="app-nav">
        <div className="nav-content">
          {[
            { id: 'pos', icon: ShoppingCart, label: 'Point of Sale', permission: 'sales' },
            { id: 'inventory', icon: Package, label: 'Inventory', permission: 'inventory' },
            { id: 'receiving', icon: Truck, label: 'Receive Stock', permission: 'inventory' },
            { id: 'sales', icon: History, label: 'Sales History', permission: 'reports' },
            { id: 'analytics', icon: BarChart3, label: 'Analytics', permission: 'reports' },
            { id: 'shops', icon: Store, label: 'Shops', permission: 'settings' },
            { id: 'users', icon: Users, label: 'Users', permission: 'settings' },
            { id: 'settings', icon: Settings, label: 'Settings', permission: 'settings' }
          ]
          .filter(tab => hasPermission(tab.permission))
          .map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`nav-tab ${activeTab === tab.id ? 'active' : ''}`}
            >
              <tab.icon size={20} />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </nav>

      {/* Main Content */}
      <main className="app-main">
        {/* POS Tab */}
        {activeTab === 'pos' && (
          <div className="pos-container">
            {/* Products Section */}
            <div className="products-section">
              <div className="section-header">
                <div>
                  <h2>Products - {currentShop.name}</h2>
                  <p className="shop-location">
                    <MapPin size={14} />
                    {currentShop.location}
                  </p>
                </div>
                <div className="search-box">
                  <Search className="search-icon" size={20} />
                  <input
                    type="text"
                    placeholder="Search products by name, SKU, or category..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                  />
                </div>
              </div>

              {/* Scanner Button */}
              <div className="scanner-section">
                <button 
                  onClick={() => simulateBarcodeScan('checkout')}
                  className="scanner-btn"
                >
                  <Scan size={20} />
                  Scan Barcode
                </button>
                <div className="scanner-info">
                  <small>Click to simulate barcode scanning for checkout</small>
                </div>
              </div>

              <div className="products-grid">
                {filteredProducts.map(product => (
                  <div key={product.id} className="product-card">
                    <div className="product-badge">
                      <span className={`stock-indicator ${product.stock < 10 ? 'low' : product.stock < 20 ? 'medium' : 'high'}`}>
                        {product.stock} left
                      </span>
                    </div>
                    <div className="product-info">
                      <h3 className="product-name">{product.name}</h3>
                      <p className="product-sku">
                        <Barcode size={12} />
                        {product.sku} • {product.category}
                      </p>
                      <div className="product-price">
                        <span className="price">{currentShop.currency}{product.price.toFixed(2)}</span>
                        {hasPermission('reports') && (
                          <span className="cost">Cost: {currentShop.currency}{product.costPrice.toFixed(2)}</span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => addToCart(product)}
                      disabled={product.stock === 0}
                      className={`add-to-cart-btn ${product.stock === 0 ? 'disabled' : ''}`}
                    >
                      {product.stock === 0 ? 'Out of Stock' : 'Add to Cart'}
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Cart Section */}
            <div className="cart-section">
              <div className="cart-header">
                <h2>Current Sale - {currentShop.name}</h2>
                <div className="cart-summary">
                  <span>{cart.length} items</span>
                  <span>{currentShop.currency}{calculateTotal().toFixed(2)}</span>
                </div>
              </div>

              {cart.length === 0 ? (
                <div className="empty-cart">
                  <ShoppingCart size={48} />
                  <p>Your cart is empty</p>
                  <span>Add products from the list or scan barcodes</span>
                </div>
              ) : (
                <>
                  <div className="cart-items">
                    {cart.map(item => (
                      <div key={item.id} className="cart-item">
                        <div className="item-info">
                          <h4>{item.name}</h4>
                          <p>{currentShop.currency}{item.price.toFixed(2)} each</p>
                          <small>SKU: {item.sku}</small>
                        </div>
                        <div className="item-controls">
                          <div className="quantity-controls">
                            <button
                              onClick={() => updateQuantity(item.id, -1)}
                              className="quantity-btn"
                            >
                              <Minus size={16} />
                            </button>
                            <span className="quantity">{item.quantity}</span>
                            <button
                              onClick={() => updateQuantity(item.id, 1)}
                              className="quantity-btn"
                            >
                              <Plus size={16} />
                            </button>
                          </div>
                          <div className="item-total">
                            <span>{currentShop.currency}{(item.price * item.quantity).toFixed(2)}</span>
                            <button
                              onClick={() => removeFromCart(item.id)}
                              className="remove-btn"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="cart-totals">
                    <div className="total-row">
                      <span>Subtotal:</span>
                      <span>{currentShop.currency}{calculateTotal().toFixed(2)}</span>
                    </div>
                    <div className="total-row">
                      <span>Tax ({currentShop.taxRate}%):</span>
                      <span>{currentShop.currency}{calculateTax().toFixed(2)}</span>
                    </div>
                    {hasPermission('reports') && (
                      <div className="total-row profit">
                        <span>Profit:</span>
                        <span>{currentShop.currency}{calculateProfit().toFixed(2)}</span>
                      </div>
                    )}
                    <div className="total-row grand-total">
                      <span>Total:</span>
                      <span>{currentShop.currency}{calculateFinalTotal().toFixed(2)}</span>
                    </div>
                  </div>

                  <div className="checkout-section">
                    <div className="customer-input">
                      <Smartphone size={20} />
                      <input
                        type="tel"
                        placeholder="Customer WhatsApp number (optional)"
                        value={customerPhone}
                        onChange={(e) => setCustomerPhone(e.target.value)}
                        className="customer-phone-input"
                      />
                    </div>
                    
                    <div className="whatsapp-info">
                      <small>
                        Receipt will be sent to {currentShop.whatsappNumbers.filter(w => w.isActive).length} active WhatsApp numbers
                      </small>
                    </div>
                    
                    <button
                      onClick={processSale}
                      className="checkout-btn"
                    >
                      <Send size={20} />
                      Complete Sale & Auto-Send WhatsApp
                    </button>
                    
                    <div className="sale-by-info">
                      <small>Sale will be recorded under: <strong>{currentUser.name}</strong></small>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Inventory Receiving Tab */}
        {activeTab === 'receiving' && hasPermission('inventory') && (
          <div className="receiving-tab">
            <div className="section-card">
              <div className="receiving-header">
                <h2>
                  <Truck size={24} />
                  Receive Inventory
                </h2>
                <p>Add new stock or update existing products</p>
                
                <button 
                  onClick={() => setInventoryReceiving({ ...inventoryReceiving, show: true })}
                  className="btn-primary large"
                >
                  <PackageOpen size={20} />
                  Open Inventory Form
                </button>
              </div>

              {/* Quick Stats */}
              <div className="inventory-stats">
                <div className="inventory-stat">
                  <Package size={24} />
                  <div>
                    <span className="stat-number">{products.length}</span>
                    <span className="stat-label">Total Products</span>
                  </div>
                </div>
                <div className="inventory-stat">
                  <AlertTriangle size={24} />
                  <div>
                    <span className="stat-number">{products.filter(p => p.stock < 10).length}</span>
                    <span className="stat-label">Low Stock</span>
                  </div>
                </div>
                <div className="inventory-stat">
                  <PackageOpen size={24} />
                  <div>
                    <span className="stat-number">{inventoryHistory.length}</span>
                    <span className="stat-label">Receipts Today</span>
                  </div>
                </div>
              </div>

              {/* Recent Inventory History */}
              <div className="inventory-history">
                <h3>Recent Inventory Receipts</h3>
                {inventoryHistory.length === 0 ? (
                  <div className="empty-history">
                    <PackageOpen size={48} />
                    <p>No inventory receipts yet</p>
                    <span>Start by receiving new inventory</span>
                  </div>
                ) : (
                  <div className="history-list">
                    {inventoryHistory.slice(0, 10).map(record => (
                      <div key={record.id} className="history-item">
                        <div className="history-info">
                          <strong>{record.productName}</strong>
                          <span>Code: {record.productCode}</span>
                          <span>Quantity: +{record.quantity}</span>
                        </div>
                        <div className="history-meta">
                          <span className={`type-badge ${record.type}`}>
                            {record.type.replace('_', ' ')}
                          </span>
                          <span>{new Date(record.timestamp).toLocaleString()}</span>
                          <span>By: {record.receivedBy}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Other tabs remain similar */}
        {/* Inventory, Sales, Analytics, Shops, Users, Settings tabs... */}
        
      </main>
    </div>
  );
};

export default WhatsAppPOSSystem;